import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Clock, DollarSign, Play, Square, Plus, AlertTriangle,
  Loader2, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

const ShiftsPage = () => {
  const { api } = useAuth();
  const [currentShift, setCurrentShift] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [startingCash, setStartingCash] = useState('');
  const [countedCash, setCountedCash] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [shiftSummary, setShiftSummary] = useState(null);
  const [processing, setProcessing] = useState(false);

  const loadData = async () => {
    try {
      const [currentRes, shiftsRes] = await Promise.all([
        api.get('/shifts/current'),
        api.get('/shifts')
      ]);
      setCurrentShift(currentRes.data);
      setShifts(shiftsRes.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to load shifts');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [api]);

  const handleOpenShift = async () => {
    setProcessing(true);
    try {
      await api.post('/shifts/open', { starting_cash: parseFloat(startingCash) || 0 });
      toast.success('Shift opened successfully');
      setShowOpenDialog(false);
      setStartingCash('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open shift');
    } finally {
      setProcessing(false);
    }
  };

  const prepareCloseShift = async () => {
    try {
      const response = await api.get(`/shifts/${currentShift.id}/summary`);
      setShiftSummary(response.data);
      setShowCloseDialog(true);
    } catch (error) {
      toast.error('Failed to load shift summary');
    }
  };

  const handleCloseShift = async () => {
    setProcessing(true);
    try {
      await api.post('/shifts/close', {
        counted_cash: parseFloat(countedCash) || 0,
        closing_notes: closingNotes || null
      });
      toast.success('Shift closed successfully');
      setShowCloseDialog(false);
      setCountedCash('');
      setClosingNotes('');
      setShiftSummary(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close shift');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="shifts-page">
      {/* Current Shift Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Current Shift
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentShift ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Badge variant="default" className="mb-2">
                    <span className="status-pulse mr-1">●</span>
                    Active
                  </Badge>
                  <div className="text-sm text-muted-foreground">
                    Started: {new Date(currentShift.opened_at).toLocaleString()}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Starting Cash: {formatCurrency(currentShift.starting_cash)}
                  </div>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={prepareCloseShift}
                  data-testid="close-shift-btn"
                >
                  <Square className="w-4 h-4 mr-2" />
                  End Shift
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">No active shift</p>
              <Button onClick={() => setShowOpenDialog(true)} data-testid="open-shift-btn">
                <Play className="w-4 h-4 mr-2" />
                Start Shift
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Shifts */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Shifts</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            {shifts.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No shifts yet</p>
            ) : (
              <div className="space-y-3">
                {shifts.map(shift => (
                  <div 
                    key={shift.id} 
                    className="p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    data-testid={`shift-${shift.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{shift.user_name}</span>
                          <Badge variant={shift.is_open ? 'default' : 'secondary'}>
                            {shift.is_open ? 'Active' : 'Closed'}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(shift.opened_at).toLocaleString()}
                          {shift.closed_at && (
                            <span> - {new Date(shift.closed_at).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm">Starting: {formatCurrency(shift.starting_cash)}</div>
                        {shift.closed_at && (
                          <>
                            <div className="text-sm">Counted: {formatCurrency(shift.counted_cash)}</div>
                            <div className={`text-sm font-medium ${shift.variance < 0 ? 'text-destructive' : shift.variance > 0 ? 'text-green-600' : ''}`}>
                              Variance: {formatCurrency(shift.variance)}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Open Shift Dialog */}
      <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start New Shift</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Starting Cash</label>
            <div className="relative mt-2">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="number"
                placeholder="0.00"
                value={startingCash}
                onChange={(e) => setStartingCash(e.target.value)}
                className="h-14 pl-11 text-xl"
                data-testid="starting-cash"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpenDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleOpenShift} disabled={processing} data-testid="confirm-open-shift">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start Shift'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Shift Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>End Shift</DialogTitle>
          </DialogHeader>
          
          {shiftSummary && (
            <div className="space-y-4 py-4">
              {/* Summary */}
              <div className="p-4 rounded-lg bg-muted space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Starting Cash:</span>
                  <span>{formatCurrency(shiftSummary.starting_cash)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Cash Sales:</span>
                  <span>+{formatCurrency(shiftSummary.payment_breakdown?.cash || 0)}</span>
                </div>
                <div className="flex justify-between font-medium border-t border-border pt-2 mt-2">
                  <span>Expected Cash:</span>
                  <span>{formatCurrency(shiftSummary.system_cash || (shiftSummary.starting_cash + (shiftSummary.payment_breakdown?.cash || 0)))}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Transactions:</span>
                  <span>{shiftSummary.total_transactions}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Sales:</span>
                  <span>{formatCurrency(shiftSummary.total_sales)}</span>
                </div>
              </div>

              {/* Counted Cash Input */}
              <div>
                <label className="text-sm font-medium">Counted Cash</label>
                <div className="relative mt-2">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={countedCash}
                    onChange={(e) => setCountedCash(e.target.value)}
                    className="h-14 pl-11 text-xl"
                    data-testid="counted-cash"
                  />
                </div>
              </div>

              {/* Variance Warning */}
              {countedCash && (
                <div className={`p-3 rounded-lg flex items-center gap-2 ${
                  parseFloat(countedCash) !== (shiftSummary.system_cash || shiftSummary.starting_cash + (shiftSummary.payment_breakdown?.cash || 0))
                    ? 'bg-yellow-500/10 text-yellow-600'
                    : 'bg-green-500/10 text-green-600'
                }`}>
                  {parseFloat(countedCash) !== (shiftSummary.system_cash || shiftSummary.starting_cash + (shiftSummary.payment_breakdown?.cash || 0)) ? (
                    <>
                      <AlertTriangle className="w-5 h-5" />
                      <span>Variance: {formatCurrency(parseFloat(countedCash) - (shiftSummary.system_cash || shiftSummary.starting_cash + (shiftSummary.payment_breakdown?.cash || 0)))}</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      <span>Cash matches expected amount</span>
                    </>
                  )}
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="text-sm font-medium">Notes (optional)</label>
                <Input
                  placeholder="Any notes about this shift..."
                  value={closingNotes}
                  onChange={(e) => setClosingNotes(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCloseShift} 
              disabled={processing || !countedCash}
              variant="destructive"
              data-testid="confirm-close-shift"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'End Shift'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftsPage;
